// Types
import type { Ref, ComputedRef } from 'vue';
import type { TDashboardStats } from '~/modules/opportunities/automated-email/types';
import type {
  TDashboardFetchInFlight,
  TDashboardPeriod,
} from '~/modules/opportunities/automated-email/dashboard/types';

// Config
import { DEFAULT_PERIOD } from '~/modules/opportunities/automated-email/dashboard/config';

// Models
import { AutomatedEmailDomainModel } from '~/modules/opportunities/automated-email/models/automated-email-domain.model';

// Composables
import { useAutomatedEmailPatch } from '~/modules/opportunities/automated-email/composables/use-automated-email-patch';

export const useDashboard = () => {
  const model: AutomatedEmailDomainModel = new AutomatedEmailDomainModel();
  const { patchId, saleType } = useAutomatedEmailPatch();

  const stats: Ref<TDashboardStats | null> = ref<TDashboardStats | null>(null);
  const period: Ref<TDashboardPeriod> = ref<TDashboardPeriod>(DEFAULT_PERIOD);
  const isLoading: Ref<boolean> = ref<boolean>(false);

  const contactsMonitored: ComputedRef<number> = computed<number>(
    () => stats.value?.contacts_monitored ?? 0
  );

  const emailsSent: ComputedRef<number> = computed<number>(() => stats.value?.emails_sent ?? 0);

  const outboxPending: ComputedRef<number> = computed<number>(
    () => stats.value?.outbox_pending ?? 0
  );

  const setStats = (value: TDashboardStats | null): void => {
    stats.value = value;
  };

  const setPeriod = (value: TDashboardPeriod): void => {
    period.value = value;
  };

  const setIsLoading = (value: boolean): void => {
    isLoading.value = value;
  };

  let fetchInFlight: TDashboardFetchInFlight | null = null;

  const setFetchInFlight = (next: TDashboardFetchInFlight | null): void => {
    fetchInFlight = next;
  };

  const getFetchInFlight = (): TDashboardFetchInFlight | null => fetchInFlight;

  const loadDashboard = async (selectedPeriod: TDashboardPeriod): Promise<void> => {
    if (patchId.value === undefined) {
      setStats(null);

      return;
    }

    setIsLoading(true);

    try {
      setStats(await model.getDashboard(selectedPeriod, patchId.value, saleType.value));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDashboard = async (): Promise<void> => {
    const inFlight: TDashboardFetchInFlight | null = getFetchInFlight();

    if (inFlight) {
      await inFlight;

      return;
    }

    const run: TDashboardFetchInFlight = loadDashboard(period.value).finally((): void => {
      setFetchInFlight(null);
    });

    setFetchInFlight(run);

    await run;
  };

  const changePeriod = async (value: TDashboardPeriod): Promise<void> => {
    setPeriod(value);
    await loadDashboard(value);
  };

  return {
    patchId,
    saleType,
    stats,
    period,
    isLoading,
    contactsMonitored,
    emailsSent,
    outboxPending,
    fetchDashboard,
    changePeriod,
  };
};
